<script setup>
import { reactive, ref, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { criarConfigBuscaParceiroInstituicaoFinanceira } from '@/search-definitions/parceiroInstituicaoFinanceiraSearch'
import { criarConfigBuscaLimiteCredito } from '@/search-definitions/limiteCreditoSearch'
import { criarConfigBuscaOperacaoFinanciamento } from '@/search-definitions/operacaoFinanciamentoSearch'
import { criarConfigBuscaDocumentoEntrada } from '@/search-definitions/documentoEntradaSearch'
import { dataHojeBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idOperacaoHedge: 0,
      idInstituicaoFinanceira: null,
      nomeInstituicaoFinanceira: '',
      idLimiteCredito: null,
      descricaoLimiteCredito: '',
      idOperacaoFinanciamento: null,
      numeroContrato: '',
      lstTipoHedge: '',
      lstTipoPosicao: 'COMPRA',
      dataFechamento: dataHojeBR(),
      dataVencimento: '',
      lstMoedaEstrangeira: '',
      valorMoedaEstrangeira: null,
      taxaSpotFechada: null,
      taxaContratada: null,
      valorPremio: null,
      observacaoLivre: '',
      exposicoes: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const mostrarAuditoria = ref(false)

const accordionState = reactive({
  identificacao: true,
  condicoes: true,
  valores: true,
  exposicoes: true
})

const exposicaoEmEdicao = ref(null)
const showModalExposicao = computed({
  get: () => !!exposicaoEmEdicao.value,
  set: (val) => { if (!val) exposicaoEmEdicao.value = null }
})

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('HEDGE_COMPLETO')) || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
})

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const configBuscaBanco = computed(() => criarConfigBuscaParceiroInstituicaoFinanceira(authStore.organizacaoAtiva))

const configBuscaLimiteCredito = computed(() => criarConfigBuscaLimiteCredito(authStore.organizacaoAtiva, item.value.idInstituicaoFinanceira))

const configBuscaFinanciamento = computed(() => criarConfigBuscaOperacaoFinanciamento(authStore.organizacaoAtiva, item.value.idInstituicaoFinanceira))

function iniciarNovaExposicao () {
  exposicaoEmEdicao.value = {
    _isNew: true,
    idDocumentoEntrada: null,
    nomeDocumentoEntrada: '',
    valorCoberto: 0
  }
}

function salvarExposicaoLocal () {
  const exp = exposicaoEmEdicao.value
  if (exp.valorCoberto <= 0) {
    emit('show-notification', { message: 'Informe um valor coberto válido.', type: 'warning' })
    return
  }

  const { _index, _isNew, ...dados } = exposicaoEmEdicao.value
  if (!_isNew && typeof _index === 'number') {
    item.value.exposicoes[_index] = dados
  } else {
    item.value.exposicoes.push(dados)
  }
  exposicaoEmEdicao.value = null
}

async function salvar () {
  if (!podeEditar.value) return

  const payload = {
    ...item.value
  }

  await submitForm('OperacaoHedge', 'idOperacaoHedge', payload)
}

</script>

<template>
  <FormularioCadastro
    module-name="Contrato de Hedge"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :titulo-registro="item.numeroContrato"
    @submit="salvar"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="mostrarAuditoria = true">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div class="row g-3">
      <!-- SEÇÃO 1: Dados do Contrato -->
      <div class="col-12">
        <div class="card border-secondary-subtle">
          <div
            class="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.identificacao = !accordionState.identificacao"
          >
            <h6 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Identificação do Contrato</h6>
            <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.identificacao">
            <div class="row g-3">
              <div class="col-md-6">
                <CampoBusca
                  id="idInstituicaoFinanceira"
                  label="Instituição Financeira"
                  v-model="item.idInstituicaoFinanceira"
                  :configuracao-busca="configBuscaBanco"
                  @selecionado="val => item.nomeInstituicaoFinanceira = val.nomeFantasia"
                  required
                  :disabled="!podeEditar || isEditing"
                />
              </div>
              <div class="col-md-6">
                <CampoInput
                  id="numeroContrato"
                  label="Número do Contrato / Referência"
                  v-model="item.numeroContrato"
                  placeholder="Ex: NDF-2024-001"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoBusca
                  id="idLimiteCredito"
                  label="Limite de Crédito Vinculado"
                  v-model="item.idLimiteCredito"
                  :configuracao-busca="configBuscaLimiteCredito"
                  @selecionado="val => item.descricaoLimiteCredito = val.descricao"
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoBusca
                  id="idOperacaoFinanciamento"
                  label="Financiamento Protegido (Hedge de Passivo)"
                  v-model="item.idOperacaoFinanciamento"
                  :configuracao-busca="configBuscaFinanciamento"
                  @selecionado="val => item.observacaoLivre = val.observacaoLivre"
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-12">
                <CampoInput
                  id="observacaoLivre"
                  label="Observações"
                  v-model="item.observacaoLivre"
                  type="textarea"
                  rows="2"
                  :disabled="!podeEditar"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 2: Condições e Datas -->
      <div class="col-md-6">
        <div class="card border-secondary-subtle">
          <div
            class="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.condicoes = !accordionState.condicoes"
          >
            <h6 class="mb-0"><i class="bi bi-gear me-2"></i>Condições Operacionais</h6>
            <i :class="['bi', accordionState.condicoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.condicoes">
            <div class="row g-3">
              <div class="col-md-6">
                <CampoBuscaLista
                  id="lstTipoHedge"
                  label="Tipo de Hedge"
                  v-model="item.lstTipoHedge"
                  tabela="OperacaoHedge"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoBuscaLista
                  id="lstTipoPosicao"
                  label="Posição"
                  v-model="item.lstTipoPosicao"
                  tabela="OperacaoHedge"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoInput
                  id="dataFechamento"
                  label="Data de Fechamento"
                  v-model="item.dataFechamento"
                  type="date"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-6">
                <CampoInput
                  id="dataVencimento"
                  label="Data de Vencimento"
                  v-model="item.dataVencimento"
                  type="date"
                  required
                  :disabled="!podeEditar"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 3: Valores e Taxas -->
      <div class="col-md-6">
        <div class="card border-secondary-subtle">
          <div
            class="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
            @click="accordionState.valores = !accordionState.valores"
          >
            <h6 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Valores e Taxas</h6>
            <i :class="['bi', accordionState.valores ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
          <div class="card-body" v-show="accordionState.valores">
            <div class="row g-3">
              <div class="col-md-4">
                <CampoBuscaLista
                  id="lstMoedaEstrangeira"
                  label="Moeda"
                  v-model="item.lstMoedaEstrangeira"
                  tabela="OperacaoHedge"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-8">
                <CampoInput
                  id="valorMoedaEstrangeira"
                  label="Valor Notional"
                  v-model="item.valorMoedaEstrangeira"
                  type="moeda"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-4">
                <CampoInput
                  id="taxaSpotFechada"
                  label="Taxa Spot (Fechamento)"
                  v-model="item.taxaSpotFechada"
                  type="decimal(4)"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-4">
                <CampoInput
                  id="taxaContratada"
                  label="Taxa Trava (Contratada)"
                  v-model="item.taxaContratada"
                  type="decimal(4)"
                  required
                  :disabled="!podeEditar"
                />
              </div>
              <div class="col-md-4">
                <CampoInput
                  id="valorPremio"
                  label="Valor do Prêmio (Opções)"
                  v-model="item.valorPremio"
                  type="moeda"
                  :disabled="!podeEditar"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEÇÃO 4: Exposições -->
      <div class="col-12">
        <div class="card border-primary-subtle">
          <div
            class="card-header bg-primary-subtle d-flex justify-content-between align-items-center cursor-pointer"
            @click="accordionState.exposicoes = !accordionState.exposicoes"
          >
            <h6 class="mb-0 text-primary-emphasis"><i class="bi bi-list-check me-2"></i>Exposições / Embarques Cobertos</h6>
            <div class="d-flex align-items-center">
              <button v-if="podeEditar" type="button" class="btn btn-sm btn-primary py-0 me-3" @click.stop="iniciarNovaExposicao">
                <i class="bi bi-plus-lg me-1"></i> Vincular Exposição
              </button>
              <i :class="['bi', accordionState.exposicoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-primary']"></i>
            </div>
          </div>
          <div class="card-body p-0" v-show="accordionState.exposicoes">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead class="table-light small">
                  <tr>
                    <th>Documento de Entrada</th>
                    <th class="text-end">Valor Coberto</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(exp, index) in item.exposicoes" :key="index">
                    <td>
                      <span v-if="exp.idDocumentoEntrada" class="badge bg-warning-subtle text-warning-emphasis">
                        <i class="bi bi-file-earmark-text me-1"></i>
                        Doc. Entrada: {{ exp.nomeDocumentoEntrada || exp.idDocumentoEntrada }}
                      </span>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td class="text-end fw-bold">{{ new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(exp.valorCoberto) }}</td>
                    <td class="text-end">
                      <button v-if="podeEditar" type="button" class="btn btn-link text-danger p-0" @click="item.exposicoes.splice(index, 1)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!item.exposicoes || item.exposicoes.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted small">
                      Nenhuma exposição vinculada. Este contrato protege o montante global.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </FormularioCadastro>

  <ModalFormulario v-model="showModalExposicao" titulo="Vincular Exposição" tamanho="md" @confirmar="salvarExposicaoLocal">
    <div v-if="exposicaoEmEdicao" class="row g-3">
      <div class="col-12">
        <CampoBusca
          id="expIdDocumentoEntrada"
          label="Documento de Entrada"
          v-model="exposicaoEmEdicao.idDocumentoEntrada"
          :configuracao-busca="criarConfigBuscaDocumentoEntrada(authStore.organizacaoAtiva)"
          required
          @selecionado="val => { exposicaoEmEdicao.nomeDocumentoEntrada = val.numeroDocumento }"
          @show-notification="p => emit('show-notification', p)"
        />
      </div>
      <div class="col-12">
        <CampoInput id="expValor" label="Valor Coberto (na Moeda do Hedge)" v-model="exposicaoEmEdicao.valorCoberto" type="moeda" required />
      </div>
    </div>
  </ModalFormulario>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="OperacaoHedge"
    :chave-primaria="item.idOperacaoHedge"
    :titulo-registro="item.numeroContrato"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>
